import { useEffect, useRef, useState } from 'react';
import {
  AnimatePresence,
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'motion/react';
import { Check, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EASE_IN_OUT, EASE_OUT } from '@/lib/motion';

/**
 * Landing hero: the product explaining itself. A client logs walking-lunge sets
 * on her phone and the trainer's dashboard picks each one up the same second:
 * the exercise ticks off, weekly volume counts up, the chart point rises and a
 * toast arrives. Built in code so it stays sharp, themed and translated.
 *
 * The loop only runs while the stage is on screen; with reduced motion it shows
 * the finished state and nothing moves.
 */

type DayState = 'done' | 'today' | 'planned' | 'rest';
const WEEK: DayState[] = ['done', 'done', 'rest', 'today', 'planned', 'planned', 'rest'];

// A lunge set is 10 reps × 2 × 14 kg = 280 kg of volume
const SET_VOLUME = 280;
const PREV_WEEK = 10300;
const HISTORY = [8400, 9100, 8800, 9900, PREV_WEEK];
const FINAL_WEEK = 11150;

/** 0: set 2 of 3 in progress · 1: set 2 logged · 2: lunges done */
type Step = 0 | 1 | 2;
const weekVolume = (step: Step) => FINAL_WEEK - (2 - step) * SET_VOLUME;

// ms from the start of a cycle
const TIMELINE: { at: number; press?: boolean; step?: Step }[] = [
  { at: 1500, press: true },
  { at: 1640, press: false, step: 1 },
  { at: 3300, press: true },
  { at: 3440, press: false, step: 2 },
];
const CYCLE = 8200;

function useLiveLoop(active: boolean, reduce: boolean) {
  const [step, setStep] = useState<Step>(reduce ? 2 : 0);
  const [pressed, setPressed] = useState(false);

  useEffect(() => {
    if (reduce) {
      setStep(2);
      return;
    }
    if (!active) return;
    const timers: number[] = [];
    const run = () => {
      setStep(0);
      setPressed(false);
      for (const ev of TIMELINE) {
        timers.push(
          window.setTimeout(() => {
            if (ev.press !== undefined) setPressed(ev.press);
            if (ev.step !== undefined) setStep(ev.step);
          }, ev.at),
        );
      }
      timers.push(window.setTimeout(run, CYCLE));
    };
    // Give the entrance a moment before the first set is logged
    timers.push(window.setTimeout(run, 700));
    return () => timers.forEach(clearTimeout);
  }, [active, reduce]);

  return { step, pressed };
}

function smoothPath(points: [number, number][]) {
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

/** Counts to the new value instead of jumping; tabular figures so nothing jitters */
function CountingNumber({ value, locale }: { value: number; locale: string }) {
  const mv = useMotionValue(value);
  const fmt = new Intl.NumberFormat(locale);
  const text = useTransform(mv, (v) => fmt.format(Math.round(v)));
  useEffect(() => {
    const controls = animate(mv, value, { duration: 0.7, ease: EASE_OUT });
    return () => controls.stop();
  }, [mv, value]);
  return <motion.span>{text}</motion.span>;
}

function VolumeChart({ step }: { step: Step }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || 'pl';
  const current = weekVolume(step);
  const series = [...HISTORY, current];
  const w = 280;
  const h = 96;
  const pad = 6;
  const min = 8000;
  const max = 11500;
  const pts: [number, number][] = series.map((v, i) => [
    pad + (i * (w - pad * 2)) / (series.length - 1),
    h - pad - ((v - min) / (max - min)) * (h - pad * 2),
  ]);
  const line = smoothPath(pts);
  const area = `${line} L ${pts[pts.length - 1][0]} ${h} L ${pts[0][0]} ${h} Z`;
  const last = pts[pts.length - 1];
  const pct = Math.round((current / PREV_WEEK - 1) * 100);
  const move = { duration: 0.6, ease: EASE_OUT };

  return (
    <div>
      <p className="text-[13px] text-muted-foreground">{t('hero.preview.volume')}</p>
      <p className="mt-1 font-mono text-2xl font-medium tabular text-foreground">
        <CountingNumber value={current} locale={locale} /> <span className="text-sm text-muted-foreground">kg</span>
      </p>
      <p className="mt-1 text-xs text-positive tabular">{t('hero.preview.volumeChange', { pct })}</p>
      {/* The chart wipes in left to right once; afterwards only the last point moves */}
      <motion.svg
        viewBox={`0 0 ${w} ${h}`}
        className="mt-3 h-24 w-full overflow-visible"
        preserveAspectRatio="none"
        aria-hidden="true"
        initial={{ clipPath: 'inset(-10px 100% -10px -10px)' }}
        animate={{ clipPath: 'inset(-10px -10px -10px -10px)' }}
        transition={{ duration: 1.2, delay: 0.5, ease: EASE_IN_OUT }}
      >
        <defs>
          <linearGradient id="hero-volume-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--brand)" stopOpacity="0.2" />
            <stop offset="100%" stopColor="var(--brand)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1="0" x2={w} y1={h * f} y2={h * f} stroke="currentColor" className="text-border" strokeDasharray="2 4" />
        ))}
        <motion.path
          initial={false}
          animate={{ d: area }}
          transition={{ d: move }}
          fill="url(#hero-volume-fill)"
        />
        <motion.path
          initial={false}
          animate={{ d: line }}
          transition={{ d: move }}
          fill="none"
          stroke="var(--brand)"
          strokeWidth="2"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        <motion.circle
          initial={false}
          animate={{ cx: last[0], cy: last[1] }}
          transition={{ cx: move, cy: move }}
          r="3.5"
          fill="var(--card)"
          stroke="var(--brand)"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </motion.svg>
      <div className="mt-2 flex justify-between font-mono text-[11px] text-muted-foreground tabular">
        {series.map((_, i) => (
          <span key={i}>{t('hero.preview.weekShort', { n: i + 1 })}</span>
        ))}
      </div>
    </div>
  );
}

/** A checkmark that draws itself: state change you can read, not a swap */
function DrawnCheck({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <motion.path
        d="M2.5 6.2 4.9 8.5 9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.35, ease: EASE_OUT }}
      />
    </svg>
  );
}

function Dashboard({ step }: { step: Step }) {
  const { t } = useTranslation();
  const days = t('hero.preview.days', { returnObjects: true }) as string[];
  const lungeDone = step === 2;
  const exercises = [
    { key: 'squat', sets: '4 × 6', load: '80 kg', state: 'done' },
    { key: 'rdl', sets: '3 × 8', load: '60 kg', state: 'done' },
    { key: 'lunge', sets: '3 × 10', load: '2 × 14 kg', state: lungeDone ? 'done' : 'active' },
    { key: 'hipThrust', sets: '3 × 12', load: '70 kg', state: 'todo' },
  ] as const;

  return (
    <div className="relative overflow-hidden rounded-2xl surface-float">
      {/* Window bar */}
      <div className="flex h-11 items-center gap-3 border-b border-border px-4">
        <div className="flex gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/20" />
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/20" />
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/20" />
        </div>
        <span className="text-xs text-muted-foreground">{t('hero.preview.windowTitle')}</span>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inset-0 rounded-full bg-positive" />
          </span>
          {t('hero.preview.live')}
        </span>
      </div>

      {/* Toast: the set the client just logged */}
      <AnimatePresence>
        {lungeDone && (
          <motion.div
            key="toast"
            initial={{ opacity: 0, y: -8, scale: 0.97, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -6, transition: { duration: 0.2, ease: EASE_OUT } }}
            transition={{ duration: 0.4, ease: EASE_OUT }}
            className="absolute right-3 top-14 z-10 flex items-center gap-2.5 rounded-xl glass px-3 py-2"
            style={{ transformOrigin: 'top right' }}
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-foreground">MN</span>
            <span className="text-[12px] text-foreground">{t('hero.preview.toast')}</span>
            <span className="font-mono text-[11px] text-muted-foreground">{t('hero.preview.now')}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid gap-px bg-border sm:grid-cols-[1.25fr_1fr]">
        <div className="bg-card p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-[13px] font-medium text-foreground">
              MN
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Marta Nowak</p>
              <p className="text-[13px] text-muted-foreground">{t('hero.preview.plan')}</p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-7 gap-1" aria-label={t('hero.preview.weekLabel')}>
            {WEEK.map((state, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5">
                <span className={`text-[11px] ${state === 'today' ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {days[i]}
                </span>
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-lg text-[11px] ${
                    state === 'done'
                      ? 'bg-foreground text-background'
                      : state === 'today'
                        ? 'border border-brand text-brand-ink'
                        : state === 'planned'
                          ? 'border border-dashed border-muted-foreground/40'
                          : 'bg-muted'
                  }`}
                >
                  {state === 'done' && <Check size={14} strokeWidth={2} />}
                  {state === 'today' && <span className="h-1.5 w-1.5 rounded-full bg-brand" />}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-5 flex items-baseline justify-between">
            <p className="text-sm font-medium text-foreground">{t('hero.preview.session')}</p>
            <p className="font-mono text-xs text-muted-foreground tabular">
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={lungeDone ? 3 : 2}
                  className="inline-block"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25, ease: EASE_OUT }}
                >
                  {lungeDone ? 3 : 2}
                </motion.span>
              </AnimatePresence>{' '}
              / 4
            </p>
          </div>
          <ul className="mt-2">
            {exercises.map((ex, i) => (
              <li
                key={ex.key}
                className={`flex h-10 items-center gap-3 ${i > 0 ? 'border-t border-border' : ''} ${i === 3 ? 'hidden sm:flex' : ''}`}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
                    ex.state === 'done'
                      ? 'bg-foreground text-background'
                      : ex.state === 'active'
                        ? 'border border-brand'
                        : 'border border-muted-foreground/40'
                  }`}
                >
                  {ex.state === 'done' && <DrawnCheck key={`${ex.key}-${step}`} />}
                </span>
                <span
                  className={`flex-1 truncate text-[13px] transition-colors duration-300 ${
                    ex.state === 'done' ? 'text-muted-foreground' : 'text-foreground'
                  }`}
                >
                  {t(`hero.preview.exercises.${ex.key}`)}
                </span>
                <span className="font-mono text-xs text-muted-foreground tabular whitespace-nowrap">
                  {ex.sets} · {ex.load}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col bg-card p-5">
          <VolumeChart step={step} />
          <div className="mt-auto hidden pt-5 sm:block">
            <div className="rounded-xl bg-muted p-3">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <MessageSquare size={12} strokeWidth={1.5} />
                <span>Marta · 18:42</span>
              </div>
              <p className="mt-1.5 text-[13px] leading-5 text-foreground">{t('hero.preview.message')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The client's side: the workout screen of the mobile app */
function Phone({ step, pressed }: { step: Step; pressed: boolean }) {
  const { t } = useTranslation();
  const current = step === 0 ? 2 : 3; // set being performed
  const finished = step === 2;

  return (
    <div className="w-[236px] rounded-[44px] bg-[#0b0b0c] p-[7px] shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_30px_60px_-20px_rgba(0,0,0,0.6),0_12px_24px_-12px_rgba(0,0,0,0.4)]">
      <div className="relative flex h-[468px] flex-col overflow-hidden rounded-[37px] bg-background px-4 pb-4">
        {/* Status bar + island */}
        <div className="relative flex h-11 shrink-0 items-center justify-between px-2 text-[11px] font-medium text-foreground">
          <span className="tabular">9:41</span>
          <span className="absolute left-1/2 top-2.5 h-[22px] w-[74px] -translate-x-1/2 rounded-full bg-black" />
          <span className="flex items-center gap-1" aria-hidden="true">
            <span className="h-2 w-3 rounded-[2px] border border-foreground/60" />
          </span>
        </div>

        <p className="mt-3 text-[11px] text-muted-foreground">{t('hero.preview.session')}</p>
        <p className="mt-1 text-[19px] font-medium leading-6 tracking-[-0.01em] text-foreground">
          {t('hero.preview.exercises.lunge')}
        </p>

        {/* Sets */}
        <div className="mt-5 flex items-center justify-between">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.p
              key={finished ? 'done' : current}
              initial={{ opacity: 0, y: 6, filter: 'blur(3px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -6, filter: 'blur(3px)' }}
              transition={{ duration: 0.3, ease: EASE_OUT }}
              className="text-[12px] text-foreground"
            >
              {finished ? t('hero.preview.phone.next') : t('hero.preview.phone.set', { n: current, total: 3 })}
            </motion.p>
          </AnimatePresence>
        </div>
        <div className="mt-2.5 grid grid-cols-3 gap-1.5">
          {[1, 2, 3].map((n) => {
            const done = n < current || finished;
            const active = n === current && !finished;
            return (
              <div key={n} className="relative h-1.5 overflow-hidden rounded-full bg-muted">
                <motion.div
                  className="absolute inset-0 origin-left rounded-full bg-brand"
                  initial={false}
                  animate={{ scaleX: done ? 1 : active ? 0.35 : 0, opacity: active ? 0.5 : 1 }}
                  transition={{ duration: 0.45, ease: EASE_OUT }}
                />
              </div>
            );
          })}
        </div>

        {/* Figures */}
        <div className="mt-5 grid grid-cols-2 gap-2">
          <div className="rounded-2xl bg-muted px-3 py-3">
            <p className="text-[11px] text-muted-foreground">{t('hero.preview.phone.reps')}</p>
            <p className="mt-1 font-mono text-[26px] font-medium leading-none tabular text-foreground">10</p>
          </div>
          <div className="rounded-2xl bg-muted px-3 py-3">
            <p className="text-[11px] text-muted-foreground">{t('hero.preview.phone.load')}</p>
            <p className="mt-1 font-mono text-[26px] font-medium leading-none tabular text-foreground">
              14<span className="ml-1 text-[13px] text-muted-foreground">kg</span>
            </p>
          </div>
        </div>

        <p className="mt-3 font-mono text-[11px] text-muted-foreground tabular">{t('hero.preview.phone.previous')}</p>

        {/* Sets logged so far: each new one slides into the log */}
        <ul className="mt-4 flex flex-col">
          <AnimatePresence initial={false}>
            {[1, 2, 3]
              .filter((n) => n < current || finished)
              .map((n) => (
                <motion.li
                  key={n}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: EASE_OUT }}
                  className="overflow-hidden"
                >
                  <span className="flex h-8 items-center gap-3 border-t border-border font-mono text-[12px] tabular text-muted-foreground">
                    <span className="w-3">{n}</span>
                    <span className="flex-1 text-foreground">10 × 14 kg</span>
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-foreground text-background">
                      <DrawnCheck size={9} />
                    </span>
                  </span>
                </motion.li>
              ))}
          </AnimatePresence>
        </ul>

        <div className="mt-auto">
          <motion.div
            animate={{ scale: pressed ? 0.96 : 1 }}
            transition={{ duration: pressed ? 0.1 : 0.25, ease: EASE_OUT }}
            className={`flex h-11 items-center justify-center gap-2 rounded-2xl text-[14px] font-medium transition-colors duration-300 ${
              finished ? 'bg-muted text-foreground' : 'bg-brand text-brand-foreground'
            }`}
          >
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={finished ? 'saved' : 'save'}
                className="inline-flex items-center gap-1.5"
                initial={{ opacity: 0, filter: 'blur(4px)', scale: 0.96 }}
                animate={{ opacity: 1, filter: 'blur(0px)', scale: 1 }}
                exit={{ opacity: 0, filter: 'blur(4px)', scale: 0.96 }}
                transition={{ duration: 0.25, ease: EASE_OUT }}
              >
                {finished && <DrawnCheck size={13} />}
                {finished ? t('hero.preview.phone.saved') : t('hero.preview.phone.save')}
              </motion.span>
            </AnimatePresence>
          </motion.div>
          <div className="mx-auto mt-3 h-1 w-24 rounded-full bg-foreground/80" />
        </div>
      </div>
    </div>
  );
}

export function HeroPreview() {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: '-10% 0px' });
  const reduce = useReducedMotion() ?? false;
  const { step, pressed } = useLiveLoop(inView, reduce);

  return (
    <div ref={ref} className="relative">
      <div className="lg:mr-[260px]">
        <p className="sr-only">{t('hero.preview.panelLabel')}</p>
        <Dashboard step={step} />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        transition={{ duration: 0.9, delay: 0.55, ease: EASE_OUT }}
        className="absolute -top-4 right-0 hidden lg:block"
      >
        <p className="sr-only">{t('hero.preview.phoneLabel')}</p>
        <Phone step={step} pressed={pressed} />
      </motion.div>
    </div>
  );
}
